/**
 * Confidence badge + field card styles — matches park-v4.html
 * `.confidence-badge`, `.host-ai-field.confidence-*`, `.host-ai-field.confirmed`
 */

export const CONFIDENCE_BADGE_LEVEL_STYLE = {
    high: {
        backgroundColor: 'rgba(16, 185, 129, 0.12)',
        color: '#047857',
    },
    medium: {
        backgroundColor: 'rgba(245, 158, 11, 0.15)',
        color: '#B45309',
    },
    low: {
        backgroundColor: 'rgba(192, 57, 43, 0.12)',
        color: '#C0392B',
    },
    // "Not Found" badge for optional fields the AI didn't infer — neutral grey.
    none: {
        backgroundColor: 'rgba(108, 117, 125, 0.12)',
        color: '#495057',
    },
};

/** Opaque badges on photo thumbnails (semi-transparent rgba shows the image through). */
export const CONFIDENCE_BADGE_SOLID_LEVEL_STYLE = {
    high: {
        backgroundColor: '#D1FAE5',
        color: '#047857',
    },
    medium: {
        backgroundColor: '#FEF3C7',
        color: '#B45309',
    },
    low: {
        backgroundColor: '#FEE2E2',
        color: '#C0392B',
    },
};

export const CONFIDENCE_BADGE_BASE_STYLE = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: '100px',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    lineHeight: 1.2,
};

export function confidenceBadgeStyle(level, { solid = false } = {}) {
    const levelStyles = solid ? CONFIDENCE_BADGE_SOLID_LEVEL_STYLE : CONFIDENCE_BADGE_LEVEL_STYLE;
    return {
        ...CONFIDENCE_BADGE_BASE_STYLE,
        ...(levelStyles[level] ?? levelStyles.low),
    };
}

export function getConfidenceBadgeLabel(level, { variant = 'default' } = {}) {
    const cap = level
        ? `${level.charAt(0).toUpperCase()}${level.slice(1)}`
        : 'Low';

    if (variant === 'info') {
        if (level === 'medium') return 'Medium · review';
        if (level === 'low') return 'Low · review';
        return 'High';
    }

    if (variant === 'withConfidence') {
        return `${cap} confidence`;
    }

    return cap;
}

/** Dot indicator color (e.g. review amenity chips). */
export const CONFIDENCE_DOT_COLOR = {
    high:   '#047857',
    medium: '#B45309',
    low:    '#C0392B',
};

/** Pending AI field cards — `.host-ai-field.confidence-medium|low` */
export const AI_FIELD_PENDING_STYLE = {
    high: {
        backgroundColor: '#FFFFFF',
        borderColor: '#DEE2E6',
    },
    medium: {
        backgroundColor: 'rgba(245, 158, 11, 0.04)',
        borderColor: 'rgba(245, 158, 11, 0.25)',
    },
    low: {
        backgroundColor: 'rgba(192, 57, 43, 0.04)',
        borderColor: 'rgba(192, 57, 43, 0.25)',
    },
    // Optional field, AI returned nothing — neutral grey (not alarming).
    none: {
        backgroundColor: '#F8F9FA',
        borderColor: '#DEE2E6',
    },
};

/** Confirmed AI field card — `.host-ai-field.confirmed` */
export const AI_FIELD_CONFIRMED_STYLE = {
    backgroundColor: 'rgba(16, 185, 129, 0.04)',
    borderColor: 'rgba(16, 185, 129, 0.25)',
};

const FIELD_STATE_BADGE_BASE = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: '100px',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    lineHeight: 1.2,
};

/** `.badge-confirmed` */
export const confirmedBadgeStyle = {
    ...FIELD_STATE_BADGE_BASE,
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    color: '#047857',
};

/** `.badge-edited` */
export const editedBadgeStyle = {
    ...FIELD_STATE_BADGE_BASE,
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
    color: '#2563EB',
};
